// Accès à l'API terrain (contrat packages/contrats/src/terrain.ts) : implémentation sur le serveur
// (client HTTP commun, jeton en mémoire, rafraîchissement) et choix du simulateur local hors
// production. Le simulateur est activé par le réglage de l'appareil, par ?simulateur=1 dans
// l'adresse (?simulateur=0 le coupe), ou automatiquement hors production quand la route
// /terrain/travail est absente. GET /terrain/travail est paginé par objet : chaque page porte son
// curseur « suivant », redemandé par missions_apres, tournees_apres ou ordres_apres.
import type { ReponsePhotoTerrain, ReponseSynchronisationTerrain, SynchronisationTerrain, TravailTerrain } from '@cc/contrats';
import { requete } from '../api/client';
import type { EnvoiPhoto, TravailAppareil } from './appareil';

export interface CurseursTravail {
  limite?: number;
  missions_apres?: string;
  tournees_apres?: string;
  ordres_apres?: string;
}

export interface ApiTerrain {
  readonly source: 'serveur' | 'simulateur';
  /** Une page de GET /terrain/travail (une page par objet, curseurs du contrat). */
  travail(curseurs?: CurseursTravail): Promise<TravailTerrain>;
  synchroniser(corps: SynchronisationTerrain): Promise<ReponseSynchronisationTerrain>;
  envoyerPhoto(photo: EnvoiPhoto): Promise<ReponsePhotoTerrain>;
}

/** Borne de sécurité du nombre de pages suivies. */
const PAGES_MAX = 20;

const OBJETS = [
  { cle: 'missions', curseur: 'missions_apres' },
  { cle: 'tournees', curseur: 'tournees_apres' },
  { cle: 'ordres_mission', curseur: 'ordres_apres' },
] as const;

/**
 * Tout le travail de l'agent : la première page, puis, tant qu'un objet a un curseur « suivant », la
 * page suivante de cet objet ; la page d'un objet déjà complet, qui recommence au début, est ignorée.
 */
export async function travailComplet(api: ApiTerrain, limite?: number): Promise<TravailAppareil> {
  const premiere = await api.travail(limite ? { limite } : {});
  const t: TravailAppareil = {
    horodatage: premiere.horodatage,
    agent: premiere.agent,
    referentiels: premiere.referentiels,
    missions: [...premiere.missions.elements],
    tournees: [...premiere.tournees.elements],
    ordres_mission: [...premiere.ordres_mission.elements],
  };
  const suivants: Record<(typeof OBJETS)[number]['cle'], string | null> = { missions: premiere.missions.suivant, tournees: premiere.tournees.suivant, ordres_mission: premiere.ordres_mission.suivant };
  for (let garde = 0; OBJETS.some((o) => suivants[o.cle]) && garde < PAGES_MAX; garde += 1) {
    const curseurs: CurseursTravail = limite ? { limite } : {};
    for (const o of OBJETS) if (suivants[o.cle]) curseurs[o.curseur] = suivants[o.cle] as string;
    const page = await api.travail(curseurs);
    if (suivants.missions) t.missions.push(...page.missions.elements);
    if (suivants.tournees) t.tournees.push(...page.tournees.elements);
    if (suivants.ordres_mission) t.ordres_mission.push(...page.ordres_mission.elements);
    for (const o of OBJETS) if (suivants[o.cle]) suivants[o.cle] = page[o.cle].suivant;
  }
  return t;
}

export const apiServeur: ApiTerrain = {
  source: 'serveur',
  travail(curseurs = {}) {
    return requete<TravailTerrain>('/terrain/travail', { parametres: { ...curseurs } });
  },
  synchroniser(corps) {
    return requete<ReponseSynchronisationTerrain>('/terrain/synchronisation', { methode: 'POST', corps });
  },
  envoyerPhoto(p) {
    const corps = new FormData();
    corps.set('id_client', p.id_client);
    corps.set('objet_type', p.objet_type);
    corps.set('objet_id_client', p.objet_id_client);
    corps.set('empreinte_sha256', p.empreinte_sha256);
    corps.set('fichier', p.fichier, p.nom);
    return requete<ReponsePhotoTerrain>('/terrain/photos', { methode: 'POST', corps });
  },
};

export const CLE_SIMULATEUR = 'cc-terrain:simulateur';

function lireReglage(): string | null {
  try {
    return window.localStorage.getItem(CLE_SIMULATEUR);
  } catch {
    return null;
  }
}

/** Le simulateur est-il demandé sur cet appareil ? Un paramètre d'adresse ?simulateur=1 ou 0 le fixe. */
export function simulateurActif(): boolean {
  if (typeof window === 'undefined') return false;
  const parametre = new URLSearchParams(window.location.search).get('simulateur');
  if (parametre === '1' || parametre === '0') activerSimulateur(parametre === '1');
  return lireReglage() === '1';
}

export function activerSimulateur(actif: boolean): void {
  try {
    if (actif) window.localStorage.setItem(CLE_SIMULATEUR, '1');
    else window.localStorage.removeItem(CLE_SIMULATEUR);
  } catch {
    // Stockage indisponible (navigation privée restreinte) : le réglage ne survit pas à la page.
  }
}

/** Hors production, l'absence de la route réelle peut basculer sur le simulateur. */
export function basculeAutomatiquePermise(): boolean {
  return import.meta.env.VITE_CC_ENVIRONNEMENT !== 'production';
}
